const errorMap = {
  'the server responded with status 500':
    'The server responded with status 500. Please try again later.',
  err_categories_is_missing:
    'Categories are missing. Please add categories and try again.',
  err_categories_must_be_filled:
    'Categories must be filled. Please add categories and try again.',
  err_icp_size_cannot_be_greater_than_1500: `ICP must be 1500 characters or less. Please shorten your ideal candidate description.`,
  'Error parsing generation content':
    'Unable to generate ICP. Please try again.',
};

const highlightItems = [
  {
    id: 'Experience',
    label: 'Experience',
    description: '“at least 5 years and not more than 8 years”',
  },

  {
    id: 'Skills',
    label: 'Skills',
    description: '“Skilled in stakeholder management and project management”',
  },
  {
    id: 'Company',
    label: 'Company',
    description: '“worked in a FAANG company or a seed-stage startup”',
  },
  {
    id: 'Industry',
    label: 'Industry',
    description: '“worked in healthcare or health tech”',
  },
  {
    id: 'Location',
    label: 'Location',
    description: '“should not be based outside of North America”',
  },
  {
    id: 'Title',
    label: 'Title',
    description: '“Senior Product Manager”',
  },
  {
    id: 'Education',
    label: 'Education',
    description: "“Bachelor's degree in Computer Science or related field”",
  },
  {
    id: 'Likely to stay',
    label: 'LIKELY TO STAY',
    description: '“an average tenure of 3 years”',
  },
];

const thresholdMap = {
  low: {
    id: 'low',
    from: 0,
    to: 49,
    label: 'Low',
    shortLabel: 'Low',
    bg: 'error-200',
    dark: 'pastel-1-dark',
    light: 'pastel-1-light',
  },
  average: {
    id: 'average',
    from: 50,
    to: 74,
    label: 'Average',
    shortLabel: 'Avg',
    bg: 'warning-200',
    dark: 'pastel-6-dark',
    light: 'pastel-6-light',
  },
  high: {
    id: 'high',
    from: 75,
    to: 100,
    label: 'High',
    shortLabel: 'High',
    bg: 'success-200',
    dark: 'pastel-2-dark',
    light: 'pastel-2-light',
  },
};

const loaderSteps = {
  icp: {
    message: {
      title: 'Generating ICP...',
      description:
        'Our AI is analyzing the job description to generate the ideal candidate profile',
    },
    steps: [
      {
        id: '1',
        label: 'Reading the job description',
      },
      {
        id: '2',
        label: 'Identifying key skills and requirements',
      },
      {
        id: '3',
        label: 'Finalizing the ideal candidate profile',
      },
    ],
  },
  attributes: {
    message: {
      title: 'Generating attributes...',
      description:
        'Our AI is analyzing the ideal candidate description to generate the attributes',
    },
    steps: [
      {
        id: '1',
        label: 'Reading the ideal candidate description',
      },
      {
        id: '2',
        label: 'Identifying the must-have’s and good-to-have’s',
      },
      {
        id: '3',
        label: 'Drafting the attributes',
      },
      {
        id: '4',
        label: 'Stack-ranking and finalizing the attributes',
      },
    ],
  },
  testing: {
    message: {
      title: 'Generating the AI score',
      description:
        'Our AI is analyzing the attributes and the candidate to generate the AI score',
    },
    steps: [
      {
        id: '1',
        label: "Analyzing the candidate's profile",
        duration: 2,
      },
      {
        id: '2',
        label: 'Scoring the candidate on each attribute',
        duration: 2,
      },
      {
        id: '3',
        label: 'Calculating the cumulative AI score',
        duration: 3,
      },
      {
        id: '4',
        label: 'Finalizing the AI score and reasoning',
      },
    ],
  },
  testingScreener: {
    message: {
      title: 'Evaluating the candidate',
      description:
        'The Scoring agent is analyzing the attributes and the candidate to generate a score',
    },
    steps: [
      {
        id: '1',
        label: "Analyzing the candidate's profile",
        duration: 2,
      },
      {
        id: '2',
        label: 'Scoring the candidate on each attribute',
        duration: 2,
      },
      {
        id: '3',
        label: 'Calculating the cumulative score',
        duration: 3,
      },
      {
        id: '4',
        label: 'Finalizing the score and reasoning',
      },
    ],
  },
};

export const AiScoringConstants = {
  STEP_ICP: 1,
  STEP_ATTRIBUTES: 2,
  STEP_TESTING: 3,
  MAX_ATTRIBUTES: 15,
  MIN_ATTRIBUTES_REQUIRED: 3,
  MIN_ATTRIBUTE_LENGTH: 10,
  DEFAULT_AUTO_REJECT_THRESHOLD: 20,
  BULK_PROGRESS_POLL_INTERVAL: 10000,
  MIN_JOB_DESCRIPTION_LENGTH: 100,
  ICP_MIN_LENGTH: 30,
  ICP_MIN_LENGTH_MESSAGE: 'Enter at least 30 characters',
  ICP_MAX_CHARACTER_LIMIT: 1500,
  MIN_ATTRIBUTE_LENGTH_MESSAGE: 'Attribute must be at least 10 characters long',
  NOT_APPLICABLE_TOOLTIP:
    'This candidate was not scored because they are no longer in the application review stages',
  NO_REVIEW_TOOLTIP: 'This candidate has not been scored',
  DUPLICATE_ATTRIBUTE_DESCRIPTION_MESSAGE:
    'This attribute is already present in the list. Update or remove it to proceed.',
  NO_ATTRIBUTES_BANNER: {
    title: 'No attributes generated',
    subtitle:
      'No attributes were generated from your ICP but you can add them manually. Add at least 3 attributes for accurate scoring.',
  },
  INSUFFICIENT_ATTRIBUTES_BANNER: {
    getMessage: (
      attributesCount: number,
      minAttributesRequired: number,
      isApiFailure: boolean = false
    ) => {
      if (isApiFailure) {
        return `You need at least ${minAttributesRequired} to score candidates accurately. Add more manually.`;
      }
      return `Only ${attributesCount} attribute${
        attributesCount > 1 ? 's' : ''
      } were generated from your ICP. You need at least ${minAttributesRequired} to score candidates accurately. Add more manually.`;
    },
  },
  gradientStyle: {
    background: 'linear-gradient(180deg, #8A90FF 0%, #B15FDA 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    textFillColor: 'transparent',
    fontVariationSettings: "'opsz' 18",
  },
  errorMap,
  highlightItems,
  thresholdMap,
  loaderSteps,
};

AiScoringConstants.MIN_ATTRIBUTE_LENGTH_MESSAGE = `Attribute must be at least ${AiScoringConstants.MIN_ATTRIBUTE_LENGTH} characters long`;
